import { Box, Button, Container, Divider, Grid, Stack, Typography, styled } from '@mui/material';
import { PublicRounded as PublicRoundedIcon } from '@mui/icons-material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../Common/_hooks/useAppSelector';
import { getHelpCenterLinks } from '../Common/helpCenter';
import HeaderLink, { HeaderNavigationProvider } from '../Header/HeaderNavigation';
import PreferencesDialog from '../Header/PreferencesDialog';

type Props = {
  clientRouterLinks?: boolean
}

type FooterLinkItem = {
  label: string
  to: string
  external?: boolean
  reloadDocument?: boolean
}

type FooterSection = {
  title: string
  links: FooterLinkItem[]
}

const FooterRoot = styled('footer')({
  background: '#080707',
  color: '#fff',
  flexShrink: 0,
  marginTop: 'auto',
  position: 'relative',
  zIndex: 1,
});

const FooterContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 0, 0),
}));

const FooterList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  margin: theme.spacing(0, 0, 2),
  padding: 0,
}));

const FooterListLink = styled(HeaderLink)(({ theme }) => ({
  color: '#fff',
  display: 'inline-block',
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  fontSize: '0.9rem',
  lineHeight: '1.75rem',
  textDecoration: 'none',

  '&:hover': {
    textDecoration: 'underline',
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 3,
  },
}));

const PreferenceButton = styled(Button)(({ theme }) => ({
  color: '#fff',
  fontSize: '0.9rem',
  fontWeight: 400,
  justifyContent: 'flex-start',
  lineHeight: '1.75rem',
  minWidth: 0,
  padding: 0,
  textTransform: 'none',

  '&:hover': {
    background: 'transparent',
    textDecoration: 'underline',
  },

  '.MuiButton-startIcon': {
    marginLeft: 0,
  },
}));

const FooterMeta = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2, 0),

  [theme.breakpoints.down('sm')]: {
    textAlign: 'center',
  },
}));

const Copyright = styled(Typography)({
  color: '#fff',
  fontSize: '12px',
});

const SocialLink = styled('a')(({ theme }) => ({
  alignItems: 'center',
  color: '#fff',
  display: 'inline-flex',
  height: 30,
  justifyContent: 'center',
  width: 38,

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 3,
  },

  img: {
    display: 'block',
  },
}));

const socialLinks = [
  {
    href: 'https://www.facebook.com/artofwhere',
    title: 'Facebook page',
    alt: 'Facebook icon',
    src: require('@resources/img/pages/home/design-2020/facebook.svg?url'),
  },
  {
    href: 'https://instagram.com/artofwhere',
    title: 'Instagram profile',
    alt: 'Instagram icon',
    src: require('@resources/img/pages/home/design-2020/instagram.svg?url'),
  },
  {
    href: 'https://www.tiktok.com/@artofwhere',
    title: 'TikTok profile',
    alt: 'TikTok icon',
    src: require('@resources/img/pages/home/design-2020/tiktok.svg?url'),
  },
  {
    href: 'https://x.com/artofwhereteam',
    title: 'X profile',
    alt: 'X icon',
    src: require('@resources/img/pages/home/design-2020/x-twitter.svg?url'),
  },
  {
    href: 'https://www.pinterest.com/artofwhere',
    title: 'Pinterest page',
    alt: 'Pinterest icon',
    src: require('@resources/img/pages/home/design-2020/pinterest.svg?url'),
  },
];

export default function Footer(props: Props) {
  const [t] = useTranslation('header');
  const [tR] = useTranslation('routes');
  const [prefDialogOpened, setPrefDialogOpened] = useState(false);

  const currency = useAppSelector(state => state.get('userData').get('prefs').get('currency'));
  const lang = useAppSelector(state => state.get('userData').get('prefs').get('lang'));
  const helpCenterLinks = getHelpCenterLinks(lang);

  const onPrefDialogOpen = useCallback(() => {
    setPrefDialogOpened(true);
  }, []);

  const onPrefDialogClose = useCallback(() => {
    setPrefDialogOpened(false);
  }, []);

  const sections: FooterSection[] = useMemo(() => [
    {
      title: t('Tools'),
      links: [
        { label: t('Design Lab'), to: tR('/create'), reloadDocument: true },
        { label: t('Order Sample Packs'), to: tR('/info/sample-packs') },
        { label: t('Pricing'), to: tR('/info/pricing'), reloadDocument: true },
        { label: t('Production and Shipping'), to: tR('/info/production-and-shipping'), reloadDocument: true },
        { label: t('Custom Labels & Packaging'), to: tR('/custom-labels-and-packaging'), reloadDocument: true },
      ],
    },
    {
      title: t('Services'),
      links: [
        { label: t('Drop Shipping'), to: tR('/drop-shipping'), reloadDocument: true },
        { label: t('Wholesale'), to: tR('/wholesale'), reloadDocument: true },
      ],
    },
    {
      title: t('Our company'),
      links: [
        { label: t('About us'), to: tR('/info/about-us'), reloadDocument: true },
        { label: t('Contact us'), to: tR('/contact-us'), reloadDocument: true },
        { label: t('Blog'), to: 'https://blog.artofwhere.com', external: true },
      ],
    },
    {
      title: t('Help'),
      links: [
        { label: t('Creating products'), to: helpCenterLinks.creatingProducts, external: true },
        //{ label: t('Store integrations'), to: helpCenterLinks.storeIntegrations, external: true },
        { label: t('Printing basics'), to: helpCenterLinks.printingBasics, external: true },
        { label: t('Production & Shipping'), to: helpCenterLinks.productionShipping, external: true },
        { label: t('All guides & FAQ'), to: helpCenterLinks.root, external: true },
      ],
    },
    {
      title: t('Policies'),
      links: [
        { label: t('Refund/Return'), to: tR('/info/return-policy'), reloadDocument: true },
        { label: t('Privacy'), to: tR('/info/privacy-policy'), reloadDocument: true },
        { label: t('Terms of Service'), to: tR('/info/terms-of-service'), reloadDocument: true },
      ],
    },
  ], [t, tR, helpCenterLinks]);

  const renderLink = useCallback((link: FooterLinkItem) => {
    if (link.external) {
      return <li key={link.label}>
        <FooterListLink to={link.to} target="_blank" rel="noopener" reloadDocument>
          {link.label}
        </FooterListLink>
      </li>;
    }

    return <li key={link.label}>
      <FooterListLink to={link.to} reloadDocument={link.reloadDocument}>
        {link.label}
      </FooterListLink>
    </li>;
  }, []);

  const renderSection = useCallback((section: FooterSection) => (
    <Stack component="section" spacing={1} key={section.title}>
      <Typography variant="h3">
        {section.title}
      </Typography>
      <FooterList>
        {section.links.map(renderLink)}
      </FooterList>
    </Stack>
  ), [renderLink]);

  const renderSocialLink = useCallback((link: typeof socialLinks[number]) => (
    <SocialLink
      href={link.href}
      key={link.href}
      rel="noopener"
      target="_blank"
      title={t(link.title)}
    >
      <img loading="lazy" src={link.src} width="18" height="18" alt={t(link.alt)} />
    </SocialLink>
  ), [t]);

  return <HeaderNavigationProvider value={{ clientRouterLinks: props.clientRouterLinks ?? false }}>
    <FooterRoot id="footer">
      <FooterContent>
        <Container maxWidth="xl">
          <Grid container spacing={{ xs: 2, md: 4 }} sx={{pb: 4}}>
            <Grid item xs={6} md={3}>
              {renderSection(sections[0])}
            </Grid>
            <Grid item xs={6} md={3}>
              <Stack spacing={2}>
                {renderSection(sections[1])}
                {renderSection(sections[2])}
              </Stack>
            </Grid>
            <Grid item xs={6} md={3}>
              {renderSection(sections[3])}
            </Grid>
            <Grid item xs={6} md={3}>
              <Stack spacing={2}>
                <Stack component="section" spacing={1}>
                  <Typography variant="h3">
                    {t('Preferences')}
                  </Typography>
                  <FooterList>
                    <li>
                      <PreferenceButton
                        disableRipple
                        onClick={onPrefDialogOpen}
                        startIcon={<PublicRoundedIcon fontSize="small" />}
                      >
                        {t('{{lang}} (${{currency}})', {
                          lang: lang === 'fr_CA' ? 'FR' : 'EN',
                          currency,
                        })}
                      </PreferenceButton>
                    </li>
                  </FooterList>
                </Stack>
                {renderSection(sections[4])}
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.18)' }} />

          <FooterMeta
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'center', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Copyright>
              {t('© {{year}} Art of Where. All rights reserved.', {
                year: new Date().getFullYear(),
              })}
            </Copyright>

            <Stack direction="row" justifyContent="center" alignItems="center">
              {socialLinks.map(renderSocialLink)}
            </Stack>
          </FooterMeta>
        </Container>
      </FooterContent>
    </FooterRoot>

    <PreferencesDialog
      opened={prefDialogOpened}
      onClose={onPrefDialogClose}
    />
  </HeaderNavigationProvider>;
}
